/**
 * Club Customization Dynamic Adapter Filtering
 *
 * For driver customization type, this script filters adapter options based on the selected Club Type:
 * - Driver, Mini Driver -> shows driver adapters
 * - 3 Wood, 5 Wood, 7 Wood -> shows fairway adapters
 */

document.addEventListener('DOMContentLoaded', function() {
    // Get customization type
    const customizationTypeEl = document.getElementById('adapter-customization-type');
    if (!customizationTypeEl) return;

    const customizationType = customizationTypeEl.getAttribute('data-customization-type');

    // Only run this logic for driver customization type
    if (customizationType !== 'driver') return;

    // Get elements
    const clubTypeSelect = document.querySelector('select[name="properties[Club Type]"]');
    const adapterProductsDataEl = document.getElementById('adapter-products-data');

    if (!clubTypeSelect || !adapterProductsDataEl) return;

    // Parse all adapter products
    let allAdapterProducts = [];
    try {
        allAdapterProducts = JSON.parse(adapterProductsDataEl.textContent);
    } catch (e) {
        console.error('Error parsing adapter products data:', e);
        return;
    }

    // Store original products for filtering
    window.allAdapterProducts = allAdapterProducts;

    /**
     * Determine which adapter type to show based on club type
     * @param {string} clubType - The selected club type
     * @returns {string} 'driver' or 'fairway'
     */
    function getAdapterTypeForClubType(clubType) {
        const driverClubTypes = ['Driver', 'Mini Driver'];
        const fairwayClubTypes = ['3 Wood', '5 Wood', '7 Wood'];

        if (driverClubTypes.includes(clubType)) {
            return 'driver';
        } else if (fairwayClubTypes.includes(clubType)) {
            return 'fairway';
        }

        // Default to driver if unknown
        return 'driver';
    }

    /**
     * Filter adapter products based on club type and update the data
     */
    function filterAdapterProducts() {
        const selectedClubType = clubTypeSelect.value;
        const requiredAdapterType = getAdapterTypeForClubType(selectedClubType);

        // Filter products by adapter type
        const filteredProducts = allAdapterProducts.filter(product => {
            return product.adapterType === requiredAdapterType;
        });

        // Update the JSON data element with filtered products
        adapterProductsDataEl.textContent = JSON.stringify(filteredProducts);

        // Update adapter brands dropdown
        updateAdapterBrands(filteredProducts);

        // Clear current adapter selection
        clearAdapterSelection();

        // Dispatch event to trigger re-rendering of adapter options
        document.dispatchEvent(new CustomEvent('adapterProductsFiltered', {
            detail: { products: filteredProducts }
        }));
    }

    /**
     * Clear adapter selection
     */
    function clearAdapterSelection() {
        const adapterValueInput = document.getElementById('adapter-model-value');
        const adapterVariantInput = document.getElementById('adapter-model-variant-id');

        if (adapterValueInput) {
            adapterValueInput.value = '';
            adapterValueInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (adapterVariantInput) {
            adapterVariantInput.value = '';
        }

        // Reset the adapter model button
        const adapterButton = document.querySelector('.adapter-model-select-button');
        if (adapterButton) {
            adapterButton.innerHTML = `
                <div class="model-select-button__placeholder">Select option</div>
                <div class="model-select-button__dropdown-icon">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            `;
        }
    }

    /**
     * Update adapter brands dropdown based on filtered products
     * @param {Array} products - Filtered adapter products
     */
    function updateAdapterBrands(products) {
        const adapterBrandSelect = document.getElementById('adapter-brand-select');
        const modalAdapterBrandSelect = document.getElementById('modal-adapter-brand');

        if (!adapterBrandSelect && !modalAdapterBrandSelect) return;

        // Get unique vendors from filtered products
        const vendors = [...new Set(products.map(p => p.vendor))].sort((a, b) =>
            a.toLowerCase().localeCompare(b.toLowerCase())
        );

        // Update both dropdowns
        [adapterBrandSelect, modalAdapterBrandSelect].forEach(select => {
            if (!select) return;

            const currentValue = select.value;

            // Clear options and add default options (All and No Adapter)
            select.innerHTML = '<option value="" checked>All</option><option value="__no_adapter__">No Adapter</option>';

            // Add vendor options
            vendors.forEach(vendor => {
                const option = document.createElement('option');
                option.value = vendor;
                option.textContent = vendor;
                select.appendChild(option);
            });

            // Restore value if still available (including __no_adapter__)
            if (vendors.includes(currentValue) || currentValue === '__no_adapter__') {
                select.value = currentValue;
            } else {
                select.value = '';
            }
        });
    }

    /**
     * Initialize the filtering on page load
     */
    function initializeAdapterFiltering() {
        // Wait a bit for the main script to initialize
        setTimeout(() => {
            // Filter products based on initial club type selection
            filterAdapterProducts();

            // Listen for club type changes
            clubTypeSelect.addEventListener('change', function() {
                filterAdapterProducts();
            });
        }, 100);
    }

    // Run initialization
    initializeAdapterFiltering();
});

/**
 * Putter Shaft Customization
 *
 * Handles putter shaft product loading, selection, and length options
 * based on putting style (Standard, Counterbalanced, Broomstick, Armlock)
 */
document.addEventListener('DOMContentLoaded', function() {
    // Get shared utilities from club-customization-util.js
    const {
        initModelSelector,
        fetchCollectionFeed,
        fetchVariantOverrideProducts,
        convertVariantsToProducts,
        _populateSelectOptions,
        putterLengths,
        lieAngles
    } = window.clubCustomizationUtils || {};

    if (!initModelSelector) {
        console.warn('Club customization utilities not available - make sure club-customization-util.js is loaded first');
        return;
    }

    /**
     * Load putter shaft products and populate vendor dropdown
     * Checks for variant override first, then falls back to collection
     * @param {string} collectionHandle - The collection handle for putter shaft products
     * @param {string} productHandle - The product handle (optional)
     * @param {string|number} variantId - The variant ID (optional)
     */
    async function loadPutterShaftProducts(collectionHandle, productHandle = null, variantId = null) {
        let products = [];
        let vendors = [];

        // First, try to load variant override products
        if (productHandle && variantId) {
            const { override_shafts } = await fetchVariantOverrideProducts(productHandle, variantId);

            if (override_shafts && Array.isArray(override_shafts) && override_shafts.length > 0) {
                products = convertVariantsToProducts(override_shafts);
                vendors = [...new Set(products.map(p => p.vendor).filter(Boolean))];
                console.log('Using variant override shafts:', products.length, 'products');
            }
        }

        // If no override products, fall back to collection feed
        if (products.length === 0 && collectionHandle) {
            const collectionData = await fetchCollectionFeed(collectionHandle);
            products = collectionData.products;
            vendors = collectionData.vendors;
            console.log('Using collection feed shafts:', products.length, 'products');
        }

        if (products.length === 0) {
            console.warn('No putter shaft products available');
            return;
        }

        // Set global variable for putter shaft products
        window.putterShaftProducts = products;

        // Sort vendors alphabetically
        const sortedVendors = vendors.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

        // Populate vendor dropdown in modal
        const modalPutterShaftBrandSelect = document.getElementById('modal-putter-shaft-brand');

        if (modalPutterShaftBrandSelect) {
            _populateSelectOptions(modalPutterShaftBrandSelect, sortedVendors, true);
        }

        // Trigger event to notify that products are loaded
        window.dispatchEvent(new CustomEvent('putterShaftProductsLoaded', {
            detail: {
                products: products,
                vendors: sortedVendors,
                isOverride: productHandle && variantId
            }
        }));
    }

    /**
     * Initialize Putter Shaft Model Selector using unified initModelSelector
     * This is a simplified version without flex/material filters
     */
    function initPutterShaftModelSelector() {
        if (!window.putterShaftProducts) return;
        initModelSelector({
            modalId: 'putter-shaft-modal',
            hiddenInputId: 'shaft-model-value',
            variantIdInputId: 'shaft-model-variant-id',
            selectButtonClass: 'putter-shaft-model-select-button',
            optionsListId: 'putter-shaft-options-list',
            searchInputId: 'putter-shaft-search-input',
            brandSelectId: null, // No main form brand select for putter shafts
            modalBrandSelectId: 'modal-putter-shaft-brand',
            optionClass: 'putter-shaft-option',
            windowDataKey: 'putterShaftProducts'
        });
    }

    /**
     * Clear putter shaft selection when variant changes
     */
    function clearPutterShaftSelection() {
        const shaftValueInput = document.getElementById('shaft-model-value');
        const shaftVariantInput = document.getElementById('shaft-model-variant-id');

        if (shaftValueInput) {
            shaftValueInput.value = '';
            shaftValueInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (shaftVariantInput) {
            shaftVariantInput.value = '';
        }

        // Reset the putter shaft model button
        const shaftButton = document.querySelector('.putter-shaft-model-select-button');
        if (shaftButton) {
            shaftButton.innerHTML = `
                <div class="model-select-button__placeholder">Select option</div>
                <div class="model-select-button__dropdown-icon">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            `;
        }
    }

    /**
     * Update putter length from variant change
     * @param {Object} variant - The selected variant object
     */
    function updatePutterLengthFromVariant(variant) {
        if (!variant) return;

        // Look for Putting Style in variant options
        let puttingStyle = null;

        // Try to find putting style in variant.options array or object
        if (Array.isArray(variant.options)) {
            puttingStyle = variant.options.find(opt =>
                opt && typeof opt === 'string' &&
                ['standard', 'counterbalanced', 'broomstick', 'armlock'].some(style =>
                    opt.toLowerCase().includes(style)
                )
            );
        } else if (variant.options && typeof variant.options === 'object') {
            // Check if options is an object with named properties
            puttingStyle = variant.options['Putting Style'] || variant.options['putting style'];
        }

        if (puttingStyle) {
            const putterShaftCollectionEl = document.querySelector('[data-putter-shaft-collection]');
            if (putterShaftCollectionEl) {
                putterShaftCollectionEl.setAttribute('data-putting-style', puttingStyle);
            }
            updatePutterLength(puttingStyle);
            updateLieAngle(puttingStyle);
        }
    }

    /**
     * Update putter length options based on putting style
     * @param {string} puttingStyle - The putting style (Standard, Counterbalanced, Broomstick, Armlock)
     */
    function updatePutterLength(puttingStyle) {
        const putterLengthSelect = document.getElementById('putter-length-select');

        if (!putterLengthSelect) return;

        // Normalize putting style to lowercase for lookup
        const normalizedStyle = puttingStyle ? puttingStyle.toLowerCase() : 'default';
        const lengths = putterLengths[normalizedStyle] || putterLengths['default'];

        // Clear current options
        putterLengthSelect.innerHTML = '';

        const option = document.createElement('option');
        option.value = "";
        option.textContent = "Select a value";
        putterLengthSelect.appendChild(option);

        // Populate with new options
        lengths.forEach(length => {
            const option = document.createElement('option');
            option.value = length;
            option.textContent = length;
            putterLengthSelect.appendChild(option);
        });

        putterLengthSelect.value = "";

        // Update all putting style indicators
        updatePuttingStyleIndicators(puttingStyle);

        // Trigger change event for summary update
        putterLengthSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Update lie angle options based on putting style
     * @param {string} puttingStyle - The putting style (Standard, Counterbalanced, Broomstick, Armlock)
     */
    function updateLieAngle(puttingStyle) {
        const lieAngleSelect = document.getElementById('lie-angle-select');

        if (!lieAngleSelect) return;

        // Normalize putting style to lowercase for lookup
        const normalizedStyle = puttingStyle ? puttingStyle.toLowerCase() : 'default';
        const angles = lieAngles[normalizedStyle] || lieAngles['default'];

        // Clear current options
        lieAngleSelect.innerHTML = '';

        const option = document.createElement('option');
        option.value = "";
        option.textContent = "Select a value";
        lieAngleSelect.appendChild(option);

        // Populate with new options
        angles.forEach(angle => {
            const option = document.createElement('option');
            option.value = angle;
            option.textContent = angle;
            lieAngleSelect.appendChild(option);
        });

        // reset value
        lieAngleSelect.value = "";

        // Trigger change event for summary update
        lieAngleSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Update all putting style indicators with the current style
     * @param {string} puttingStyle - The putting style to display
     */
    function updatePuttingStyleIndicators(puttingStyle) {
        const styleIndicators = document.querySelectorAll('.putting-style-indicator');
        styleIndicators.forEach(indicator => {
            if (puttingStyle) {
                indicator.textContent = ` (${puttingStyle})`;
            } else {
                indicator.textContent = '';
            }
        });
    }

    /**
     * Initialize lie angle based on putting style from data attribute
     */
    function initLieAngle() {
        const putterShaftCollectionEl = document.querySelector('[data-putter-shaft-collection]');
        if (!putterShaftCollectionEl) return;

        const puttingStyle = putterShaftCollectionEl.getAttribute('data-putting-style');
        if (puttingStyle) {
            updateLieAngle(puttingStyle);
        }
    }

    /**
     * Initialize putter length based on putting style from data attribute
     */
    function initPutterLength() {
        const putterShaftCollectionEl = document.querySelector('[data-putter-shaft-collection]');
        if (!putterShaftCollectionEl) return;

        const puttingStyle = putterShaftCollectionEl.getAttribute('data-putting-style');
        if (puttingStyle) {
            updatePutterLength(puttingStyle);
        }
    }

    // Initialize putter shaft products if collection handle is provided
    const putterShaftCollectionEl = document.querySelector('[data-putter-shaft-collection]');
    if (putterShaftCollectionEl) {
        const putterShaftCollectionHandle = putterShaftCollectionEl.getAttribute('data-putter-shaft-collection');
        const productHandle = putterShaftCollectionEl.getAttribute('data-product-handle');
        const variantId = putterShaftCollectionEl.getAttribute('data-variant-id');
        loadPutterShaftProducts(putterShaftCollectionHandle, productHandle, variantId);
    }

    // Initialize putter shaft model selector when products are loaded
    window.addEventListener('putterShaftProductsLoaded', () => {
        if (window.putterShaftProducts) {
            initPutterShaftModelSelector();
        }
    });

    // Reload putter shaft products when variant changes
    document.addEventListener('variant:change', (event) => {
        const putterShaftCollectionEl = document.querySelector('[data-putter-shaft-collection]');
        if (putterShaftCollectionEl && event.detail?.variant?.id) {
            const putterShaftCollectionHandle = putterShaftCollectionEl.getAttribute('data-putter-shaft-collection');
            const productHandle = putterShaftCollectionEl.getAttribute('data-product-handle');
            const newVariantId = event.detail.variant.id;

            // Update the data attribute with new variant ID
            putterShaftCollectionEl.setAttribute('data-variant-id', newVariantId);

            // Clear current shaft selection to prevent old values from lingering
            clearPutterShaftSelection();

            // Reload putter shaft products with new variant
            loadPutterShaftProducts(putterShaftCollectionHandle, productHandle, newVariantId);

            // Update putter length if putting style changed in variant
            updatePutterLengthFromVariant(event.detail.variant);
        }
    });

    /**
     * Clear putter grip selection
     */
    function clearPutterGripSelection() {
        const gripValueInput = document.getElementById('grip-model-value');
        const gripVariantInput = document.getElementById('grip-model-variant-id');

        if (gripValueInput) {
            gripValueInput.value = '';
            gripValueInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (gripVariantInput) {
            gripVariantInput.value = '';
        }

        // Reset the grip model button
        const gripButton = document.querySelector('.grip-model-select-button');
        if (gripButton) {
            gripButton.innerHTML = `
                <div class="model-select-button__placeholder">Select option</div>
                <div class="model-select-button__dropdown-icon">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            `;
        }
    }

    // Reload putter grip products when variant changes (for putter customization)
    document.addEventListener('variant:change', (event) => {
        const gripCollectionEl = document.querySelector('[data-grip-collection][data-product-handle]');
        if (gripCollectionEl && event.detail?.variant?.id) {
            const gripCollectionHandle = gripCollectionEl.getAttribute('data-grip-collection');
            const productHandle = gripCollectionEl.getAttribute('data-product-handle');
            const newVariantId = event.detail.variant.id;

            // Update the data attribute with new variant ID
            gripCollectionEl.setAttribute('data-variant-id', newVariantId);

            // Clear current grip selection to prevent old values from lingering
            clearPutterGripSelection();

            // Dispatch event to reload grip products with new variant
            window.dispatchEvent(new CustomEvent('loadGripCollection', {
                detail: {
                    collectionHandle: gripCollectionHandle,
                    customizationType: 'putters',
                    productHandle: productHandle,
                    variantId: newVariantId
                }
            }));
        }
    });

    // Initialize putter length and lie angle on page load
    initPutterLength();
    initLieAngle();

    // ========================================
    // Alignment Mark Dropdowns
    // ========================================

    /**
     * Initialize all alignment mark dropdowns
     */
    function initAlignmentDropdowns() {
        const triggers = document.querySelectorAll('.alignment-dropdown-trigger[data-dropdown]');

        triggers.forEach(trigger => {
            const dropdownType = trigger.getAttribute('data-dropdown');
            const panel = document.querySelector(`.alignment-dropdown-panel[data-panel="${dropdownType}"]`);
            const hiddenInput = document.getElementById(`alignment-mark-${dropdownType}-value`);

            if (!panel || !hiddenInput) return;

            const options = panel.querySelectorAll('.alignment-dropdown-option');

            // Toggle dropdown on trigger click
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const isOpen = panel.classList.contains('open');

                // Close all other dropdowns first
                closeAllAlignmentDropdowns();

                if (!isOpen) {
                    openAlignmentDropdown(trigger, panel);
                }
            });

            // Handle option selection
            options.forEach(option => {
                option.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    selectAlignmentOption(option, trigger, panel, hiddenInput);
                });
            });

            // Keyboard navigation
            trigger.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    trigger.click();
                } else if (e.key === 'Escape') {
                    closeAlignmentDropdown(trigger, panel);
                }
            });
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            const isInsideDropdown = e.target.closest('.alignment-dropdown-trigger') ||
                                     e.target.closest('.alignment-dropdown-panel');
            if (!isInsideDropdown) {
                closeAllAlignmentDropdowns();
            }
        });
    }

    /**
     * Open a specific alignment dropdown
     */
    function openAlignmentDropdown(trigger, panel) {
        trigger.setAttribute('aria-expanded', 'true');
        panel.classList.add('open');
    }

    /**
     * Close a specific alignment dropdown
     */
    function closeAlignmentDropdown(trigger, panel) {
        trigger.setAttribute('aria-expanded', 'false');
        panel.classList.remove('open');
    }

    /**
     * Close all alignment dropdowns
     */
    function closeAllAlignmentDropdowns() {
        document.querySelectorAll('.alignment-dropdown-trigger[data-dropdown]').forEach(trigger => {
            trigger.setAttribute('aria-expanded', 'false');
        });
        document.querySelectorAll('.alignment-dropdown-panel').forEach(panel => {
            panel.classList.remove('open');
        });
    }

    /**
     * Select an alignment option
     * @param {HTMLElement} option - The selected option element
     * @param {HTMLElement} trigger - The trigger button
     * @param {HTMLElement} panel - The dropdown panel
     * @param {HTMLElement} hiddenInput - The hidden input to store value
     */
    function selectAlignmentOption(option, trigger, panel, hiddenInput) {
        const value = option.getAttribute('data-value');
        const name = option.getAttribute('data-name');
        const image = option.getAttribute('data-image');

        // Update hidden input value
        hiddenInput.value = value;

        // Update all options' aria-selected state within this panel
        panel.querySelectorAll('.alignment-dropdown-option').forEach(opt => {
            opt.setAttribute('aria-selected', 'false');
        });
        option.setAttribute('aria-selected', 'true');

        // Update trigger button content
        const content = trigger.querySelector('.alignment-dropdown-trigger__content');
        if (content) {
            let html = '';

            if (image) {
                html += `<div class="alignment-dropdown-trigger__image"><img src="${image}" alt="${name}"></div>`;
            }
            html += `<span class="alignment-dropdown-trigger__text">${name}</span>`;

            content.innerHTML = html;
        }

        // Close dropdown
        closeAlignmentDropdown(trigger, panel);

        // Trigger change event for validation/summary
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Initialize alignment dropdowns
    initAlignmentDropdowns();
});
